/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { LanguageService, LogService, RbacService, UtilsService } from '../../../../../../core/services';
import { Observable, Subscriber } from 'rxjs';
import { SNMPConfigurationApiService } from '../../../../shared/http';
import { simplePasswordValidation } from '../../../../../../core/directives/validation';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-snmp-user',
    templateUrl: './snmp-user.component.html'
})
export class SnmpUserComponent extends BaseComponent {
    /**
     * Stored form
     */
    public formGroup: FormGroup;

    /**
     * Store the user id if applicable.
     */
    public editId: string = null;

    /**
     * Path back to previous page.
     */
    public pathBack: string = '';

    /**
     * Store the list of users.
     */
    public users: any[] = [];

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store initial form data.
     * @private
     */
    private initialFormData: any;

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private logService: LogService,
        private rbacService: RbacService,
        private router: Router,
        private snmpConfigApiService: SNMPConfigurationApiService,
        private utilsService: UtilsService
    ) {
        super();

        this.editId = this.activatedRoute.snapshot.paramMap.get('id');
        this.pathBack = this.editId ? '../../../' : '..';

        this.setUpForm();
        this.setUpFormSubscribers();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Add SNMP user.
     */
    public submit(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const data = this.formGroup.value;
            const apiCall: Observable<any> = this.editId
                ? this.snmpConfigApiService.editSNMPUser(this.editId, data)
                : this.snmpConfigApiService.addSNMPUser(data);
            apiCall
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.router.navigate([this.pathBack], { relativeTo: this.activatedRoute })
                            .then((): void => {
                            }).catch();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('SNMPConfig: addConfig failed', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up the form.
     * @private
     */
    private setUpForm(): void {
        this.formGroup = this.formBuilder.group({
            id: null,
            username: [null, Validators.required],
            authAlgorithm: ['MD5'],
            authPassphrase: [null, [Validators.required, simplePasswordValidation]],
            privacyAlgorithm: ['AES'],
            privacyPassphrase: [null, [Validators.required, simplePasswordValidation]],
            writeAccess: [false, Validators.required]
        });
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.formGroup.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (values): void => {
                    this.utilsService.debounce('form', () => {
                        const match: boolean = this.users.some(
                            item => item.username.toLowerCase() === values.username.toLowerCase() &&
                                item.id !== this.editId
                        );
                        if (match) {
                            const usernameField: AbstractControl = this.formGroup.get('username');
                            usernameField.setErrors({ 'duplicateName': `${this.languageService.translate('duplicate_name')}` });
                            usernameField.markAsTouched();
                        }

                        if (!this.utilsService.hasFormDataChanged(this.initialFormData, values)) {
                            this.formGroup.markAsPristine();
                        }
                    });
                }
            });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        this.snmpConfigApiService.getAllSNMPUsers()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (users: any): void => {
                    this.users = users;
                    if (this.editId) {
                        const patchUser = users.find((user: any): boolean => user.id === this.editId);
                        this.formGroup.patchValue(patchUser);
                    }

                    this.initialFormData = this.formGroup.value;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('SNMPConfigComponent: setUpPageData failed to fetch data ', error.message);
                }
            });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'snmp-config-user-update': [{ path: '/snmp', methods: ['PUT'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup['snmp-config-user-update']) {
            this.formGroup.disable();
        }
    }
}
